<script lang="ts">
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';

  type Persona = {
    title: string;
    tagline: string;
    body: string;
    href: string;
    icon: 'workflow' | 'shield' | 'code' | 'connectors' | 'stack';
  };

  // Top row — three personas shipping AI inside one company.
  const insiders: Persona[] = [
    {
      title: 'Ops & Product Teams',
      tagline: 'Ship AI without a six-month procurement cycle.',
      body: 'Working agents, shaped to your workflows. When legal asks about residency, the answer is architectural. When the CFO asks about cost, it\u2019s a marketplace subscription — not a capital project.',
      href: '/use-cases/ops-product',
      icon: 'workflow',
    },
    {
      title: 'IT & Security Teams',
      tagline: 'Govern everything. Block nothing.',
      body: 'Full visibility into every agent, every action, every data flow. Entra ID native. Sandbox-to-production promotion. The governance layer your org needs before anyone ships AI safely.',
      href: '/use-cases/it-security',
      icon: 'shield',
    },
    {
      title: 'Developers & Engineers',
      tagline: 'Stop rebuilding the stack. Start building what\u2019s yours.',
      body: 'API-first. MCP and OpenAPI extension points. Replaceable UI. Override anything. Every hour of your time goes into the AI that makes your company unique — not rebuilding the seven layers everyone else is rebuilding.',
      href: '/use-cases/developers',
      icon: 'code',
    },
  ];

  // Bottom row — two personas building AI for others.
  const builders: Persona[] = [
    {
      title: 'Service Providers & SIs',
      tagline: 'You deliver the 20% that wins the deal.',
      body: 'Corral is the 80% foundation you can extend into any client tenant. Your IP, their cloud, your margin. White-label ready. Repeatable deployments so you stop rebuilding the foundation for every engagement.',
      href: '/use-cases/service-providers',
      icon: 'connectors',
    },
    {
      title: 'Product Companies',
      tagline: 'Build a product. Not a platform.',
      body: 'Orchestration, security, governance, infrastructure — invisible underneath your product. Multi-tenant SaaS or per-customer deployment, your choice. Your team focuses on the domain expertise that makes your product unique.',
      href: '/use-cases/product-companies',
      icon: 'stack',
    },
  ];
</script>

<section id="who-its-for" class="bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />

  <GridContent>
    <GridInner>
      <div class="py-[80px] lg:py-[110px]">
        <!-- Header -->
        <div class="flex flex-col items-start lg:items-center text-left lg:text-center mb-14 lg:mb-16 max-w-[820px] mx-auto">
          <p class="font-mono text-[12px] uppercase tracking-[0.12em] text-[#ee6018] mb-5">
            05 · Who It&rsquo;s For
          </p>
          <h2 class="font-display font-[300] text-[40px] md:text-[52px] lg:text-[60px] leading-[1.02] tracking-[-0.035em] text-[#020202] mb-6">
            Built for the people who actually ship AI inside companies.
          </h2>
          <p class="font-body font-light text-[17px] lg:text-[19px] leading-[1.55] text-[#5C5855] max-w-[640px]">
            Not just developers. Not just the CISO. The ops manager automating workflows. The IT lead governing what everyone else is building. The SI delivering AI to their clients. The product company shipping AI features instead of AI plumbing.
          </p>
        </div>

        <!-- Row eyebrow -->
        <p class="font-mono text-[11px] uppercase tracking-[0.14em] text-[#5C5855] mb-6 max-w-[900px] mx-auto">
          Inside your company —
        </p>

        <!-- Top row: 3 peer personas (insiders).
             Cards extend grid-line to grid-line (negative inset cancels
             GridInner's 54px padding) with gaps between them for
             visual separation. -->
        <div class="relative mb-14 lg:mb-16 lg:-mx-[54px]">
          <div class="hidden lg:block absolute top-0 -left-[9.03vw] -right-[9.03vw] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[9px] top-[-9px]" />
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[9px] top-[-9px]" />

          <div class="hidden lg:block absolute bottom-0 -left-[9.03vw] -right-[9.03vw] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[9px] bottom-[-9px]" />
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[9px] bottom-[-9px]" />

          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {#each insiders as p, i}
              <a
                href={p.href}
                class="persona-card bg-white border border-[#D3D3D3] p-7 lg:p-8 group relative flex flex-col"
              >
                <Star variant="bottom-right" color="#D3D3D3" class="hidden lg:block absolute -left-px -top-px" />
                <Star variant="bottom-left" color="#D3D3D3" class="hidden lg:block absolute -right-px -top-px" />
                <Star variant="top-right" color="#D3D3D3" class="hidden lg:block absolute -left-px -bottom-px" />
                <Star variant="top-left" color="#D3D3D3" class="hidden lg:block absolute -right-px -bottom-px" />

                <CornerFrame
                  class="w-[40px] h-[40px] flex items-center justify-center mb-6"
                  bg="bg-transparent"
                  borderColor="rgba(0, 0, 0, 0.18)"
                  cornerColor="#020202"
                  cornerSize={6}
                >
                  <div class="w-[16px] h-[16px] text-[#020202]">
                    {#if p.icon === 'workflow'}
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <rect x="2" y="2" width="4" height="4" stroke="currentColor" stroke-width="1.2" />
                        <rect x="10" y="2" width="4" height="4" stroke="currentColor" stroke-width="1.2" />
                        <rect x="6" y="10" width="4" height="4" stroke="currentColor" stroke-width="1.2" />
                        <path d="M4 6L7 10M12 6L9 10" stroke="currentColor" stroke-width="1.2" />
                      </svg>
                    {:else if p.icon === 'shield'}
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 2L3 4V8C3 11.3 5.2 14 8 14.5C10.8 14 13 11.3 13 8V4L8 2Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" />
                        <path d="M6 8L7.5 9.5L10.5 6.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    {:else if p.icon === 'code'}
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M5 4L2 8L5 12" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M11 4L14 8L11 12" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M9 3L7 13" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                      </svg>
                    {/if}
                  </div>
                </CornerFrame>

                <h3 class="font-display font-[350] text-[20px] lg:text-[22px] tracking-[-0.02em] text-[#020202] mb-3">
                  {p.title}
                </h3>
                <p class="font-mono text-[12px] text-[#ee6018] mb-4 leading-[1.4]">
                  {p.tagline}
                </p>
                <p class="font-body font-light text-[14.5px] leading-[1.55] text-[#5C5855] mb-6">
                  {p.body}
                </p>
                <span class="mt-auto font-mono text-[12px] text-[#282828] inline-flex items-center gap-1.5 group-hover:text-[#ee6018] transition-colors">
                  See the use case
                  <span aria-hidden="true" class="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </a>
            {/each}
          </div>
        </div>

        <!-- Row eyebrow -->
        <p class="font-mono text-[11px] uppercase tracking-[0.14em] text-[#5C5855] mb-6 max-w-[900px] mx-auto">
          And the ones building AI for others —
        </p>

        <!-- Bottom row: 2 builders, same grid-line-to-grid-line pattern. -->
        <div class="relative lg:-mx-[54px]">
          <div class="hidden lg:block absolute top-0 -left-[9.03vw] -right-[9.03vw] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[9px] top-[-9px]" />
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[9px] top-[-9px]" />

          <div class="hidden lg:block absolute bottom-0 -left-[9.03vw] -right-[9.03vw] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[9px] bottom-[-9px]" />
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[9px] bottom-[-9px]" />

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {#each builders as p, i}
              <a
                href={p.href}
                class="persona-card bg-white border border-[#D3D3D3] p-7 lg:p-10 group relative flex flex-col"
              >
                <Star variant="bottom-right" color="#D3D3D3" class="hidden lg:block absolute -left-px -top-px" />
                <Star variant="bottom-left" color="#D3D3D3" class="hidden lg:block absolute -right-px -top-px" />
                <Star variant="top-right" color="#D3D3D3" class="hidden lg:block absolute -left-px -bottom-px" />
                <Star variant="top-left" color="#D3D3D3" class="hidden lg:block absolute -right-px -bottom-px" />

                <div class="flex items-start gap-5 mb-5">
                  <CornerFrame
                    class="w-[44px] h-[44px] flex items-center justify-center shrink-0"
                    bg="bg-transparent"
                    borderColor="rgba(0, 0, 0, 0.18)"
                    cornerColor="#020202"
                    cornerSize={6}
                  >
                    <div class="w-[18px] h-[18px] text-[#020202]">
                      {#if p.icon === 'connectors'}
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <circle cx="4" cy="4" r="2" stroke="currentColor" stroke-width="1.2" />
                          <circle cx="14" cy="4" r="2" stroke="currentColor" stroke-width="1.2" />
                          <circle cx="4" cy="14" r="2" stroke="currentColor" stroke-width="1.2" />
                          <circle cx="14" cy="14" r="2" stroke="currentColor" stroke-width="1.2" />
                          <circle cx="9" cy="9" r="2.5" stroke="currentColor" stroke-width="1.2" />
                          <path d="M5.5 5.5L7.5 7.5M12.5 5.5L10.5 7.5M5.5 12.5L7.5 10.5M12.5 12.5L10.5 10.5" stroke="currentColor" stroke-width="1.1" />
                        </svg>
                      {:else if p.icon === 'stack'}
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path d="M9 2L2 5.5L9 9L16 5.5L9 2Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" />
                          <path d="M2 9L9 12.5L16 9" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" />
                          <path d="M2 12.5L9 16L16 12.5" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" />
                        </svg>
                      {/if}
                    </div>
                  </CornerFrame>
                  <div>
                    <h3 class="font-display font-[350] text-[22px] lg:text-[24px] tracking-[-0.02em] text-[#020202] mb-2">
                      {p.title}
                    </h3>
                    <p class="font-mono text-[12px] text-[#ee6018] leading-[1.4]">
                      {p.tagline}
                    </p>
                  </div>
                </div>
                <p class="font-body font-light text-[15px] leading-[1.55] text-[#5C5855] mb-6 max-w-[460px]">
                  {p.body}
                </p>
                <span class="mt-auto font-mono text-[12px] text-[#282828] inline-flex items-center gap-1.5 group-hover:text-[#ee6018] transition-colors">
                  See the use case
                  <span aria-hidden="true" class="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </a>
            {/each}
          </div>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<style>
  .persona-card {
    transition: background-color 160ms ease-out;
  }
  .persona-card:hover {
    background-color: #fdfdfb;
  }
</style>
